import React, { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import { hotjar } from 'react-hotjar';
import OneSignal from 'react-onesignal';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as ScanIcon } from '@assets/icons/scan.svg';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';
import TechPackView from '@components/TechPackView';

import Notification from '@features/notification/Notification';
import {
	ACTIVE_TAB_OBJ,
	NOTIFICATIONS,
	UNSEEN_COUNT,
	UNSEEN_COUNT_OBJ
} from '@features/notification/utils/Utils';

import DesignView from '@pages/design/DesignView';

import AddNewSection from '@layouts/AddNewSection';
import TopBar from '@layouts/partials/TopBar';

import { clearGlobalFilter } from '@store/actions/FilterAction';
import { _storeData } from '@store/actions/NotificationAction';
import { onTogglePrice } from '@store/actions/SystemPreferenceAction';
import { fetchTotalLikeCount } from '@store/favourite/actions';

import ApiClient from '@lib/apiClient';

import {
	addImageSuffix,
	encodeIpToken,
	getStompCLient,
	getUserInfo,
	isShowPriceOnDesign,
	isValidJSON,
	onErrorImageLoad,
	printApiError
} from '@utils/Util';

import { getOneSignalAppId, hjid, hjsv, isProductionServer } from '@/constants/constant';

import Scan from '@/features/Scan';
import { resetScanStore } from '@/features/Scan/redux/scanSlice';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

let stompClient = getStompCLient();
stompClient.reconnectDelay = 1000;
// stompClient.debug = (msg) => {
// 	if (isItLocalServer()) {
// 		console.log(msg);
// 	}
// };

const DefaultLayout = ({ id }) => {
	const [state, setState] = useState({
		showNotification: false,
		isToggleModal: null,
		orderOfMenus: []
	});
	const [isShowScan, setIsShowScan] = useState(false);
	const [isConfirm, setIsConfirm] = useState(false);
	const wrapperRef = useRef();
	const notificationRef = useRef();
	const [currentActivePage, setCurrentActivePage] = useState('');

	const { notifications, unseenCount, unseenCountObj, notificationTabName } = useSelector(
		(store) => store.notification
	);
	const loaderStore = useSelector((store) => store.loaderStore);
	// const favouriteCount = useSelector((store) => store.favourite.totalFavouriteCount);
	const designViewModalStore = useSelector((store) => store.designViewModalStore);
	const { scannedItems } = useSelector((store) => store.scanStore);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { likeCount } = useSelector((state) => state.favouritePageStore);

	useEffect(() => {
		setCurrentActivePage(location.pathname);
	}, [location.pathname]);

	const getGeoLocation = async () => {
		try {
			const response = await fetch('https://api.ipify.org/?format=json');
			const geoLocationInfo = await response.json();
			if (getUserInfo()?.id) {
				let encodedToken = encodeIpToken(geoLocationInfo.ip);
				let { data } = await ApiClient.GET('getAuthGL', `?token=${encodedToken}`);
				sessionStorage.setItem('glInfo', JSON.stringify(data));
			}
		} catch (e) {
			printApiError(e);
		}
	};

	const handleClickOutside = (event) => {
		if (wrapperRef && !wrapperRef.current.contains(event.target)) {
			setState((prev) => ({
				...prev,
				showNotification: false
			}));
			dispatch(_storeData('notificationIconActive', true));
		}
	};

	const toggleShowModel = (notificationID) => {
		setState((prev) => ({
			...prev,
			isToggleModal: notificationID
		}));
	};

	const show = () => {
		setState((prevState) => ({
			...prevState,
			showNotification: !prevState.showNotification
		}));
		dispatch(_storeData('notificationIconActive', false));
	};

	const initializeHotJar = () => {
		if (isProductionServer()) {
			hotjar.initialize(hjid, hjsv);
		}
	};

	useEffect(() => {
		getGeoLocation();
		let reload = localStorage.getItem('isReload');
		if (reload === 'true') {
			localStorage.setItem('isReload', 'false');
			navigate(0);
		}
		localStorage.setItem('subscriptionStatus', true);
		setState((prev) => ({ ...prev, orderOfMenus: JSON.parse(localStorage.getItem('orderOfMenus')) }));

		dispatch(fetchTotalLikeCount());
		setupOneSignal();
		window.addEventListener('mousedown', handleClickOutside);
		getUnseenNotificationCount();
		initializeHotJar();
		ReactGA.initialize('UA-168122648-1');
		let showPice = isShowPriceOnDesign();
		dispatch(onTogglePrice(showPice));
		notificationRef.current = designViewModalStore?.id;

		return () => {
			window.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener('popstate', () => dispatch(clearGlobalFilter()));
		};
	}, []);

	const getUnseenNotificationCount = async () => {
		await ApiClient.GET('getUnseenNotificationCount')
			.then(({ data }) => {
				dispatch(_storeData('unseenCount', data));
			})
			.catch(() => {});
	};

	const setupOneSignal = async () => {
		try {
			let userInfo = localStorage.getItem('userInfo');
			if (userInfo) {
				userInfo = JSON.parse(userInfo);
			} else {
				userInfo = {};
			}

			await OneSignal.init({ appId: getOneSignalAppId() });
			setTimeout(async () => {
				await OneSignal?.setExternalUserId(userInfo?.id?.toString());
				await OneSignal.on('notificationDisplay', (event) => {
					let { data } = event;
					if (data) {
						data = data.content && isValidJSON(data.content) ? JSON.parse(data.content) : {};
						let tempNotification = notifications;
						let unSeenCountObj = unseenCountObj;
						let unSeenCount = parseInt(unseenCount);
						if (data.isGroup && data.groupByKey !== undefined && data.groupByKey !== null) {
							let groupByNotification = notifications.find(
								(notification) => notification.groupByKey === data.groupByKey
							);
							if (groupByNotification !== undefined) {
								if (groupByNotification.isSeen === false) {
									unSeenCountObj[data.frontEndNotificationEvent.toLowerCase()] -= 1; // front-end category wise count 1 minus
									unSeenCountObj[ACTIVE_TAB_OBJ.all.value.toLowerCase()] -= 1; // front-end all category count 1 minus
									unSeenCount -= 1; // total count 1 minus
								}
								tempNotification = notifications.filter((notification) => {
									return notification.groupByKey !== data.groupByKey;
								});
							}
						}

						unSeenCountObj[data.frontEndNotificationEvent.toLowerCase()] += 1; // front-end category wise count 1 plus
						unSeenCountObj[ACTIVE_TAB_OBJ.all.value.toLowerCase()] += 1; // front-end all category count 1 plus
						dispatch(_storeData(UNSEEN_COUNT_OBJ, unSeenCountObj)); // save
						dispatch(_storeData(UNSEEN_COUNT, unSeenCount + 1)); // save

						if (
							data.frontEndNotificationEvent.toLowerCase() ===
								notificationTabName.toLowerCase() ||
							ACTIVE_TAB_OBJ.all.value.toLowerCase() === notificationTabName.toLowerCase()
						)
							dispatch(_storeData(NOTIFICATIONS, [data, ...tempNotification]));
					}
					oneSignalDataParse(data);
				});
				await OneSignal.on('subscriptionChange', (isSubscribed) => {
					localStorage.setItem('subscriptionStatus', isSubscribed ? 'subscribed' : 'notSubscribed');
					if (isSubscribed) {
						OneSignal.setExternalUserId(userInfo.id.toString());
					}
				});
			}, 1500);
		} catch {
			/* empty */
		}
	};

	const oneSignalDataParse = (data) => {
		if (data) {
			let content = data.content && isValidJSON(data.content) ? JSON.parse(data.content) : {};
			let tempNotification = notifications;
			let unSeenCountObj = unseenCountObj;
			let unSeenCount = parseInt(unseenCount);
			if (content) {
				if (content?.isGroup && content?.groupByKey !== undefined && content?.groupByKey !== null) {
					let groupByNotification = notifications.find(
						(notification) => notification.groupByKey === content.groupByKey
					);

					if (groupByNotification !== undefined) {
						if (groupByNotification.isSeen === false) {
							unSeenCountObj[content.frontEndNotificationEvent.toLowerCase()] -= 1; // front-end category wise count 1 minus
							unSeenCountObj[ACTIVE_TAB_OBJ.all.value.toLowerCase()] -= 1; // front-end all category count 1 minus
							unSeenCount -= 1; // total count 1 minus
						}
						tempNotification = notifications.filter((notification) => {
							return notification.groupByKey !== content.groupByKey;
						});
					}
				} else {
					tempNotification = notifications.filter((notification) => {
						return notification.id !== content.id;
					});
				}

				unSeenCountObj[content.frontEndNotificationEvent.toLowerCase()] += 1; // front-end category wise count 1 plus
				unSeenCountObj[ACTIVE_TAB_OBJ.all.value.toLowerCase()] += 1; // front-end all category count 1 plus
				dispatch(_storeData(UNSEEN_COUNT_OBJ, unSeenCountObj)); // save
				dispatch(_storeData(UNSEEN_COUNT, unSeenCount + 1)); // save

				if (
					content.frontEndNotificationEvent.toLowerCase() === notificationTabName.toLowerCase() ||
					ACTIVE_TAB_OBJ.all.value.toLowerCase() === notificationTabName.toLowerCase()
				) {
					dispatch(_storeData(NOTIFICATIONS, [content, ...tempNotification]));
				}
			}
		}
	};

	const logout = async () => {
		try {
			OneSignal.removeExternalUserId();
			localStorage.removeItem('orderOfMenus');
			await stompClient.disconnect(function () {});
			navigate('/logout');
		} catch {
			//console.log(e.message);
		}
	};

	let userInfo = localStorage.getItem('userInfo');
	if (userInfo) {
		userInfo = JSON.parse(userInfo);
	}
	let { showNotification } = state;

	const getFavouriteIcon = () => {
		return currentActivePage.startsWith('/favourite/') ? 'favorite.svg' : 'not-favorite.svg';
	};

	const handleCloseScan = () => {
		setIsShowScan(false);
		dispatch(resetScanStore());
	};

	return (
		<LoaderComponent loading={loaderStore}>
			<div className="content">
				<nav className="navbar navbar-expand navbar-light topbar static-top">
					<TopBar
						stompClient={stompClient}
						showTotalStyles={true}
						location={location.pathname}
						detailPageId={id}
					/>

					<ul className="navbar-nav ml-auto align-items-center">
						<li className="cursor-pointer">
							<ScanIcon onClick={() => setIsShowScan(true)} />
						</li>
						{userInfo?.primaryserType !== 'COSTING_MANAGER' && (
							<li>
								<AddNewSection />
							</li>
						)}
						<li
							className="nav-item ml-2 added-favorite cursor-pointer"
							onClick={() => {
								navigate('/favourite/list');
							}}
						>
							<CommonImage src={`/icons/${getFavouriteIcon()}`} alt="love" />
							{likeCount > 0 && <span className="favorite-count">{likeCount}</span>}
						</li>
						<li
							className="nav-item notification-bell dropdown no-arrow ml-1 mr-3"
							data-toggle="tooltip"
							data-placement="top"
							title=""
							data-original-title="Notifications"
							ref={wrapperRef}
						>
							<button
								className="btn btn-outline-default nav-link"
								type="button"
								id="dropdownNotification"
								onClick={show}
							>
								<img
									src={`${process.env.PUBLIC_URL}/images/icons/notification-bell.png`}
									alt="notification"
								/>
								{unseenCount ? (
									<span className="notification-count">
										{unseenCount < 100 ? unseenCount : '99+'}
									</span>
								) : (
									<></>
								)}
							</button>
							{showNotification ? (
								<div>
									<Notification
										toggleModel={toggleShowModel}
										isToggleModel={state.isToggleModal}
									/>
								</div>
							) : (
								<></>
							)}
						</li>

						{/* Nav Item - User Information */}
						<li className="nav-item dropdown no-arrow">
							<button
								className="btn btn-outline-default nav-link dropdown-toggle"
								type="button"
								id="dropdownProfileButton"
								data-toggle="dropdown"
								aria-haspopup="true"
								aria-expanded="false"
							>
								{userInfo && userInfo.profilePicDocument ? (
									<img
										className="img-profile rounded-circle"
										src={addImageSuffix(userInfo.profilePicDocument.docUrl, '_xicon')}
										onError={(e) =>
											onErrorImageLoad(e, userInfo.profilePicDocument.docUrl)
										}
										alt="profile_image"
									/>
								) : (
									<img
										className="img-profile rounded-circle"
										src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
										alt="profile_image"
									/>
								)}
							</button>
							{/* Dropdown - User Information */}
							<div
								className="dropdown-menu dropdown-menu-right shadow animated--grow-in"
								aria-labelledby="dropdownProfileButton"
							>
								{/* MENU_DASHBOARD */}
								{state.orderOfMenus.includes('MENU_DASHBOARD') && (
									<Link className="dropdown-item" to="/dashboard">
										<i className="fas fa-tachometer-alt mr-2 text-gray-400 font-12"></i>
										Dashboard
									</Link>
								)}
								<Link className="dropdown-item" to="/my-profile">
									<i className="fas fa-user fa-sm fa-fw mr-2 text-gray-400"></i>
									Profile
								</Link>
								<Link className="dropdown-item" to="/release-notes">
									<i className="fas fa-question-circle mr-2 text-gray-400" />
									Release Notes
								</Link>
								<a
									className="dropdown-item cursor-pointer"
									target="_blank"
									href="https://docs.google.com/document/d/1TkvGVCi0MLmea2XRlmBhGXJKCBgrr58iO_XOoWCxdfE/edit"
									rel="noreferrer"
								>
									<i className="fas fa-question-circle mr-2 text-gray-400" />
									Tutorials
								</a>

								<div className="dropdown-divider"></div>
								<a className="dropdown-item cursor-pointer" onClick={() => logout()}>
									<i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400"></i>
									Logout
								</a>
							</div>
						</li>
					</ul>
				</nav>

				<div className="wraper container-fluid dashboard-container bg-gray">
					<Outlet />
					{designViewModalStore?.id && !designViewModalStore?.isShowMergeTechPack && <DesignView />}
					{designViewModalStore?.id && designViewModalStore?.isShowMergeTechPack && (
						<TechPackView />
					)}
				</div>
				{isShowScan && (
					<Scan
						onClose={() => {
							if (scannedItems?.length > 0) {
								setIsConfirm(true);
							} else {
								handleCloseScan();
							}
						}}
					/>
				)}
			</div>
			{isConfirm && scannedItems?.length > 0 && (
				<DeleteConfirmationModal
					title="Close Scan Pannel"
					description="Are you sure about closing the scan pannel?"
					onClose={() => setIsConfirm(false)}
					onConfirm={() => {
						handleCloseScan();
						setIsConfirm(false);
					}}
				/>
			)}
		</LoaderComponent>
	);
};

export default DefaultLayout;
